import { autoUpdate, offset, shift, useFloating } from "@floating-ui/react";
import {
    faCalendarDays,
    faChevronDown,
    faChevronLeft,
    faChevronRight,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover } from "@headlessui/react";
import { useMemo, useState } from "react";
import { InputButton } from "../../../ui/Buttons";
import { defaultAnalyticsFilters } from "../state";
import {
    getCurrentQuarter,
    getQuartersForYear,
    isQuarterSelectable,
    parseQuarter,
} from "../utils/quarterUtils";

interface QuarterPickerProps {
    startQuarter: string; // Format: "2024-Q1"
    onChangeStartQuarter: (q: string) => void;
    endQuarter: string; // Format: "2024-Q1"
    onChangeEndQuarter: (q: string) => void;
}

interface QuarterSectionProps {
    label: string;
    year: number;
    onYearChange: (year: number) => void;
    selectedQuarter: string;
    onSelectQuarter: (quarter: string) => void;
    mode: "start" | "end";
    otherBound: string;
    currentQuarter: string;
    currentYear: number;
}

const QuarterSection = (props: QuarterSectionProps) => {
    const quarters = getQuartersForYear(props.year);

    return (
        <div
            className={`flex-1 p-3 ${props.mode === "start" ? "bg-ae-gray-50" : ""}`}
        >
            {/* Section Header */}
            <div className="text-sm font-bold text-neutral-700 mb-3">
                {props.label}
            </div>

            {/* Year Navigation */}
            <div className="flex items-center justify-between mb-3">
                <InputButton
                    onClick={() => props.onYearChange(props.year - 1)}
                    className="px-2 h-7"
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </InputButton>
                <span className="text-lg font-medium">{props.year}</span>
                <InputButton
                    onClick={() => props.onYearChange(props.year + 1)}
                    disabled={props.year >= props.currentYear}
                    className="px-2 h-7"
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </InputButton>
            </div>

            {/* Quarter Grid */}
            <div className="grid grid-cols-2 gap-2">
                {quarters.map((quarterStr) => {
                    const isSelected = quarterStr === props.selectedQuarter;
                    const isSelectable = isQuarterSelectable(
                        quarterStr,
                        props.mode,
                        props.otherBound,
                        props.currentQuarter,
                    );

                    return (
                        <button
                            key={quarterStr}
                            onClick={() => props.onSelectQuarter(quarterStr)}
                            disabled={!isSelectable}
                            className={`
                                h-9 rounded text-sm border transition-colors
                                ${
                                    isSelected
                                        ? "bg-ae-blue-550 text-white border-ae-blue-550"
                                        : !isSelectable
                                          ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                          : "bg-white border-ae-gray-250 hover:border-ae-blue-400 hover:bg-ae-gray-50"
                                }
                            `}
                        >
                            {quarterStr.split("-")[1]}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export const QuarterPicker = (props: QuarterPickerProps) => {
    const isDefault =
        props.startQuarter === defaultAnalyticsFilters.quarterFrom &&
        props.endQuarter === defaultAnalyticsFilters.quarterTo;

    const currentQuarter = useMemo(() => getCurrentQuarter(), []);
    const currentYear = useMemo(
        () => parseQuarter(currentQuarter).year,
        [currentQuarter],
    );

    // Independent display years for each section
    const [startDisplayYear, setStartDisplayYear] = useState(
        () => parseQuarter(props.startQuarter).year,
    );
    const [endDisplayYear, setEndDisplayYear] = useState(
        () => parseQuarter(props.endQuarter).year,
    );

    // Floating UI setup for popover positioning
    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "bottom-end",
        middleware: [
            offset({
                mainAxis: 3,
                crossAxis: 0,
            }),
            shift(),
        ],
        whileElementsMounted: autoUpdate,
    });

    return (
        <div ref={refs.setReference}>
            <Popover>
                {({ close }) => (
                    <>
                        <Popover.Button
                            className={`
                                flex items-center gap-2 px-3 h-8 text-sm border
                                rounded bg-white hover:bg-ae-gray-100
                                ${isDefault ? "border-ae-gray-250" : "border-ae-blue-550 bg-ae-blue-100"}
                            `}
                        >
                            <FontAwesomeIcon
                                icon={faCalendarDays}
                                className="w-4 text-ae-blue-550"
                            />
                            <span className="text-neutral-600 hidden lg:block whitespace-nowrap">
                                {props.startQuarter} → {props.endQuarter}
                            </span>
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className="w-3"
                            />
                        </Popover.Button>
                        <Popover.Panel
                            ref={refs.setFloating}
                            style={floatingStyles}
                            onKeyUp={(key) => {
                                if (key.code === "Enter") {
                                    close();
                                }
                            }}
                            className="min-w-[480px] overflow-auto rounded-md border-2 border-ae-slate-300 drop-shadow z-50 bg-white"
                        >
                            <div className="flex">
                                <QuarterSection
                                    label="Start Quarter"
                                    year={startDisplayYear}
                                    onYearChange={setStartDisplayYear}
                                    selectedQuarter={props.startQuarter}
                                    onSelectQuarter={props.onChangeStartQuarter}
                                    mode="start"
                                    otherBound={props.endQuarter}
                                    currentQuarter={currentQuarter}
                                    currentYear={currentYear}
                                />

                                <div className="w-px bg-ae-slate-300" />

                                <QuarterSection
                                    label="End Quarter"
                                    year={endDisplayYear}
                                    onYearChange={setEndDisplayYear}
                                    selectedQuarter={props.endQuarter}
                                    onSelectQuarter={props.onChangeEndQuarter}
                                    mode="end"
                                    otherBound={props.startQuarter}
                                    currentQuarter={currentQuarter}
                                    currentYear={currentYear}
                                />
                            </div>
                        </Popover.Panel>
                    </>
                )}
            </Popover>
        </div>
    );
};
